import { Button, Tag, Text } from "@ttc3k/trekker";
import {
  EnumPriceTierPriceType,
  EnumProductPublicationStatus,
  EnumRoomType,
  Product,
} from "gql/generated";
import { Check, EditPencil } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { SmartImage } from "shared/components/atoms";
import {
  formatPrice,
  getLowestNightlyPrice,
  getMultilingualString,
  isEventProduct,
} from "shared/utils";
import { Box, Flex, styled } from "styled-system/jsx";
import { ProductTypeTag } from "./ProductTypeTag";

type ProductSummaryCardProps = {
  product: Product;
  isSelected?: boolean;
  displayStatusTag?: boolean;
  onEditClick?: () => void;
  onSelectClick?: () => void;
};

const CardContainer = styled(Flex, {
  base: {
    borderRadius: "100",
    bg: "white",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "border.light",
    outlineWidth: "1px",
    outlineStyle: "solid",
    outlineColor: "transparent",
    justifyContent: "space-between",
    overflow: "hidden",
    transitionProperty: "border-color, outline-color",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-in-out",
  },
  variants: {
    visual: {
      default: {},
      selected: {
        borderColor: "border.dark",
        outlineColor: "border.dark",
      },
      draft: {
        borderStyle: "dashed",
        bg: "bg.wa.lightest",
      },
    },
  },
});

export const ProductSummaryCard = ({
  product,
  isSelected,
  displayStatusTag = false,
  onEditClick,
  onSelectClick,
}: ProductSummaryCardProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "BOOKING_WIDGET_SETUP",
  });
  const bedroomCount =
    product?.accommodationMetadata?.rooms.filter(
      room => room?.type === EnumRoomType.Bed,
    ).length ?? 0;

  const status = product.publicationStatus;
  const isDraft = status === EnumProductPublicationStatus.Draft;
  const isEvent = isEventProduct(product);
  const isPerGuestPricing = product?.pricing?.tiers.some(
    tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
  );
  const lowestNightlyPrice = getLowestNightlyPrice(product?.pricing);

  const priceText = isPerGuestPricing
    ? `${t("STEPS.1.STARTING_AT")} $${formatPrice(lowestNightlyPrice ?? 0)}/${t("STEPS.1.NIGHT")}`
    : `$${formatPrice(product?.pricing?.tiers[0]?.price ?? 0)}/${t("STEPS.1.NIGHT")}`;

  // Events price by ticket tiers, not nightly; show the lowest ticket price.
  const eventTierPrices = (product?.productTiers ?? [])
    .map(tier => tier?.price)
    .filter((price): price is number => typeof price === "number" && price > 0);
  const lowestEventPrice =
    eventTierPrices.length > 0
      ? Math.min(...eventTierPrices)
      : (product?.sellingConfig?.basePrice ?? 0);
  const eventPriceText =
    lowestEventPrice > 0 ? `$${formatPrice(lowestEventPrice)}` : "";

  return (
    <CardContainer
      visual={isSelected ? "selected" : isDraft ? "draft" : "default"}
    >
      <Flex
        flexDirection={"column"}
        gap={"100"}
        p={"300"}
        justifyContent={"space-between"}
      >
        {/* Details */}
        <Flex flexDirection={"column"} gap={"100"}>
          <Flex alignItems={"center"} gap={"100"} flexWrap={"wrap"}>
            <Text visual={"bodySemiBold"} color={"text.dark"}>
              {getMultilingualString(product?.name ?? {})}
            </Text>
            <ProductTypeTag product={product} />
          </Flex>
          {isEvent
            ? eventPriceText && (
                <Text visual={"smallMedium"} color={"text.light"}>
                  {eventPriceText}
                </Text>
              )
            : (
                <Text visual={"smallMedium"} color={"text.light"}>
                  {priceText}
                  &nbsp;•&nbsp;
                  {t("STEPS.1.BEDROOM", {
                    count: bedroomCount,
                  })}
                </Text>
              )}
        </Flex>

        {/* Actions */}
        <Flex gap={"100"}>
          {onSelectClick && (
            <Button
              mode={isSelected ? "dark" : "light"}
              Icon={isSelected ? Check : undefined}
              _hover={isSelected ? { bg: "bg.darkest" } : undefined}
              cursor={isSelected ? "unset" : undefined}
              onClick={isSelected ? undefined : onSelectClick}
              disabled={status !== EnumProductPublicationStatus.Approved}
              size={"sm"}
            >
              {isSelected ? t("PRODUCT.SELECTED") : t("PRODUCT.SELECT")}
            </Button>
          )}
          {onEditClick && (
            <Button
              mode={"light"}
              Icon={EditPencil}
              onClick={onEditClick}
              size={"sm"}
            />
          )}
        </Flex>
      </Flex>

      {/* Image (events have no product image) */}
      {!isEvent && (
        <Box position={"relative"} h={"full"} w={"auto"}>
          <SmartImage
            image={product?.images[0]}
            width={160}
            height={180}
            shape="square"
          />
          {displayStatusTag && (
            <Tag
              position={"absolute"}
              top={"250"}
              right={"250"}
              visual={
                isDraft
                  ? "warning"
                  : status === EnumProductPublicationStatus.Approved
                    ? "success"
                    : "primary"
              }
            >
              {t(
                `PRODUCT.STATUS.${status && [EnumProductPublicationStatus.Approved, EnumProductPublicationStatus.Draft].includes(status) ? status : "NOT_READY"}`,
              )}
            </Tag>
          )}
        </Box>
      )}
    </CardContainer>
  );
};
